import { useCallback, useMemo, useState, useEffect } from 'react';
import { useAtomValue } from 'jotai';
import {
  Stack,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Chip,
  ListItemText,
  OutlinedInput,
  Box,
  InputAdornment,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import Edit from '@mui/icons-material/Edit';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CloseIcon from '@mui/icons-material/Close';
import Delete from '@mui/icons-material/Delete';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import { abyssColors } from '@/theme/colors';
import { FullWidthPanel } from './styles';
import { GenericDialog } from '../../shared/GenericDialog';
import { assemblyTemplate } from '../state';
import { removeTypename, titleToKey, useSnackBarMessage } from '../utils';
import {
  AllTemplatesForBuilderDocument,
  GetVersionsDocument,
  ReviewPanelConfig,
  TemplateTypeEnum,
  useUpdateTemplateMutation,
} from '@/__generated__/graphql';

export const ReviewPanelConfigForm = ({ structureId }: { structureId: string }) => {
  const selectedAssemblyTemplate = useAtomValue(assemblyTemplate);
  const [reviewPanelConfig, setReviewPanelConfig] = useState<ReviewPanelConfig[]>([]);
  const [mode, setMode] = useState<'ReadOnly' | 'Edit'>('ReadOnly');
  const [originalReviewPanelConfig, setOriginalReviewPanelConfig] = useState<ReviewPanelConfig[]>(
    []
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [deleteGroupIds, setDeleteGroupIds] = useState<string[]>([]);

  const [newPanel, setNewPanel] = useState<{
    active: boolean;
    groupName: string;
    tables: { title: string; keys: string[] }[];
  }>({
    active: false,
    groupName: '',
    tables: [],
  });

  useEffect(() => {
    const config = removeTypename(selectedAssemblyTemplate?.reviewPanelConfig) ?? [];
    if (mode !== 'Edit') {
      setReviewPanelConfig(config);
      setOriginalReviewPanelConfig(config);
    }
  }, [selectedAssemblyTemplate, mode]);

  const allFieldKeys = useMemo(() => {
    return selectedAssemblyTemplate?.fields?.map((field) => field.key) ?? [];
  }, [selectedAssemblyTemplate]);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const handleEdit = useCallback(() => {
    setOriginalReviewPanelConfig(JSON.parse(JSON.stringify(reviewPanelConfig)));
    setMode('Edit');
  }, [reviewPanelConfig]);

  const handleCancel = useCallback(() => {
    setReviewPanelConfig(originalReviewPanelConfig);
    setNewPanel({ active: false, groupName: '', tables: [] });
    setMode('ReadOnly');
  }, [originalReviewPanelConfig]);

  const removeTableFromGroup = useCallback((groupIndex: number, sectionIndex: number) => {
    setReviewPanelConfig((prev) =>
      prev?.map((g, gIndex) =>
        gIndex !== groupIndex
          ? g
          : { ...g, groupSection: g.groupSection.filter((_, sIndex) => sIndex !== sectionIndex) }
      )
    );
  }, []);

  const updateGroupName = useCallback((groupIndex: number, value: string) => {
    setReviewPanelConfig((prev) => {
      if (!prev) return prev;
      const next = [...prev];
      next[groupIndex] = { ...next[groupIndex], groupName: value, groupKey: titleToKey(value) };
      return next;
    });
  }, []);

  const updateSectionTitle = useCallback(
    (groupIndex: number, sectionIndex: number, value: string) => {
      setReviewPanelConfig((prev) => {
        if (!prev) return prev;

        const next = [...prev];
        const group = { ...next[groupIndex] };
        const sections = [...group.groupSection];

        const sec = sections[sectionIndex];
        if (!sec) return prev;

        const fieldKeys = sec.fieldKeys ?? [];

        sections[sectionIndex] = {
          ...sec,
          title: value,
          sectionKey: sec.sectionKey,
          fieldKeys,
        };

        group.groupSection = sections;
        next[groupIndex] = group;
        return next;
      });
    },
    []
  );

  const updateSectionKeys = useCallback(
    (groupIndex: number, sectionIndex: number, keys: string[]) => {
      setReviewPanelConfig((prev) => {
        if (!prev) return prev;

        const next = [...prev];
        const group = { ...next[groupIndex] };
        const sections = [...group.groupSection];

        const sec = sections[sectionIndex];
        if (!sec) return prev;

        const updated = {
          ...sec,
          fieldKeys: keys ?? [],
        };

        sections[sectionIndex] = updated;
        group.groupSection = sections;
        next[groupIndex] = group;
        return next;
      });
    },
    []
  );

  const addNewTableToGroup = useCallback((groupIndex: number) => {
    setReviewPanelConfig((prev) => {
      if (!prev) return prev;

      const next = [...prev];
      const group = { ...next[groupIndex] };
      const sections = [...group.groupSection];

      const title = 'New table';
      sections.push({
        id: '',
        title,
        sectionKey: titleToKey(title),
        fieldKeys: [],
      });

      group.groupSection = sections;
      next[groupIndex] = group;
      return next;
    });
  }, []);

  const handleAddNewPanel = useCallback(() => {
    setNewPanel({ active: true, groupName: '', tables: [] });
  }, []);

  const addNewPanelTable = useCallback(() => {
    setNewPanel((panel) => ({ ...panel, tables: [...panel.tables, { title: '', keys: [] }] }));
  }, []);

  const updateNewPanelTableTitle = useCallback((index: number, value: string) => {
    setNewPanel((panel) => {
      const tables = [...panel.tables];
      tables[index] = { ...tables[index], title: value };
      return { ...panel, tables };
    });
  }, []);

  const updateNewPanelTableKeys = useCallback((index: number, keys: string[] | string) => {
    const arr = Array.isArray(keys) ? keys : keys.split(',').map((x) => x.trim());
    setNewPanel((panel) => {
      const tables = [...panel.tables];
      tables[index] = { ...tables[index], keys: arr };
      return { ...panel, tables };
    });
  }, []);

  const removeNewPanelTable = useCallback((index: number) => {
    setNewPanel((panel) => {
      const tables = [...panel.tables];
      tables.splice(index, 1);
      return { ...panel, tables };
    });
  }, []);

  const commitNewPanel = useCallback(() => {
    const trimmedName = newPanel.groupName.trim();
    if (!trimmedName) return;

    const groupKey = titleToKey(trimmedName);
    const sections = newPanel.tables
      .map((t) => ({
        title: t.title.trim() || 'New table',
        keys: t.keys,
      }))
      .map(({ title, keys }) => ({
        id: '',
        title,
        sectionKey: titleToKey(title),
        fieldKeys: [...keys],
      }));

    setReviewPanelConfig((prev) => {
      const next: ReviewPanelConfig[] = [
        ...(prev ?? []),
        {
          id: '',
          groupName: trimmedName,
          groupKey,
          groupSection: sections,
        },
      ];
      return next;
    });

    setNewPanel({ active: false, groupName: '', tables: [] });
  }, [newPanel.groupName, newPanel.tables]);

  const [updateReviewPanelConfig] = useUpdateTemplateMutation({
    refetchQueries: [AllTemplatesForBuilderDocument, GetVersionsDocument],
    onCompleted: (response) => {
      setDeleteGroupIds([]);
      setIsDeleteDialogOpen(false);
      setMode('ReadOnly');
      if (!response?.updateTemplate) {
        showSnackBar('An error occurred while updating review panel configuration', 'error');
        return;
      }
      showSnackBar('Review Panel Configuration saved successfully', 'success');
    },
    onError: (error) => {
      console.error(error);
      setDeleteGroupIds([]);
      setIsDeleteDialogOpen(false);
      showSnackBar(error.message, 'error');
    },
  });

  const saveReviewPanelConfig = useCallback(async () => {
    if (!reviewPanelConfig) return;

    const payload = reviewPanelConfig.map((group) => ({
      id: group.id ?? '',
      groupName: group.groupName,
      groupKey: titleToKey(group.groupName),
      groupSection: (group.groupSection ?? [])
        .filter((section): section is NonNullable<typeof section> => Boolean(section))
        .map((section) => ({
          id: section.id ?? '',
          title: section.title,
          sectionKey: titleToKey(section.title ?? section.sectionKey),
          fieldKeys: section.fieldKeys ?? [],
        })),
    }));

    setNewPanel({ active: false, groupName: '', tables: [] });

    await updateReviewPanelConfig({
      variables: {
        input: {
          type: TemplateTypeEnum.Assembly,
          structureId,
          name: 'Pipes',
          reviewPanelConfig: payload,
          lastModifiedFor: 'Update Review Panel Config',
        },
      },
    });
  }, [reviewPanelConfig, structureId, updateReviewPanelConfig]);

  const handleDeleteGroup = useCallback(() => {
    setReviewPanelConfig((prev) => prev?.filter((_, i) => !deleteGroupIds.includes(i.toString())));
    saveReviewPanelConfig();
  }, [deleteGroupIds, saveReviewPanelConfig]);

  const newPanelConfig = useMemo(() => {
    return (
      <>
        {!newPanel.active ? (
          <Stack direction="row" justifyContent="flex-start">
            <Button
              startIcon={<AddCircleIcon />}
              variant="contained"
              onClick={handleAddNewPanel}
              disabled={mode === 'ReadOnly'}
              sx={containedButtonStyle}
            >
              Add New Panel
            </Button>
          </Stack>
        ) : (
          <Stack
            sx={{
              p: 2,
              gap: 1.5,
              borderRadius: 1,
              bgcolor: abyssColors.neutral[50],
              border: `1px solid ${abyssColors.neutral[300]}`,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              New Panel
            </Typography>

            <TextField
              label="Group name"
              value={newPanel.groupName}
              onChange={(e) => setNewPanel((p) => ({ ...p, groupName: e.target.value }))}
            />

            <Divider />

            <Stack gap={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Tables
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddCircleIcon />}
                  variant="outlined"
                  onClick={addNewPanelTable}
                >
                  Add Table
                </Button>
              </Stack>

              {newPanel.tables.length === 0 ? (
                <Typography variant="body2" color={abyssColors.primary[500]}>
                  No tables yet. Click "Add Table" to start.
                </Typography>
              ) : (
                newPanel.tables.map((table, index) => (
                  <Stack
                    key={index}
                    sx={{
                      p: 2,
                      gap: 1,
                      borderRadius: 1,
                      bgcolor: abyssColors.neutral[50],
                      border: `1px solid ${abyssColors.neutral[300]}`,
                    }}
                  >
                    <Stack direction="row" alignItems="center" gap={1}>
                      <TextField
                        label="Title"
                        value={table.title}
                        onChange={(e) => updateNewPanelTableTitle(index, e.target.value)}
                        sx={{ flex: 1 }}
                      />
                      <Button
                        variant="text"
                        sx={{ color: abyssColors.error[700] }}
                        onClick={() => removeNewPanelTable(index)}
                      >
                        Remove
                      </Button>
                    </Stack>

                    <FormControl fullWidth sx={{ height: '50%' }}>
                      <InputLabel>Field keys</InputLabel>
                      <Select
                        multiple
                        value={table.keys}
                        onChange={(e) => updateNewPanelTableKeys(index, e.target.value as string[])}
                        input={<OutlinedInput label="Field keys" />}
                        renderValue={(selected) => {
                          const values = selected as string[];
                          return (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {values.map((v) => (
                                <Chip
                                  key={v}
                                  label={v}
                                  size="small"
                                  color="primary"
                                  onMouseDown={(e) => e.stopPropagation()}
                                />
                              ))}
                            </Box>
                          );
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: { maxHeight: 300, overflowY: 'auto' },
                          },
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          marginThreshold: 8,
                          disableScrollLock: true,
                        }}
                      >
                        {allFieldKeys.map((key) => (
                          <MenuItem key={key} value={key ?? ''}>
                            <Checkbox checked={table.keys.includes(key ?? '')} />
                            <ListItemText primary={key} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                ))
              )}
            </Stack>

            <Stack direction="row" gap={1} justifyContent="flex-start">
              <Button
                variant="contained"
                sx={containedButtonStyle}
                onClick={commitNewPanel}
                startIcon={<AddCircleIcon />}
                disabled={!newPanel.groupName.trim()}
              >
                Add Panel
              </Button>
              <Button
                variant="outlined"
                sx={textButtonStyle}
                onClick={() => setNewPanel({ active: false, groupName: '', tables: [] })}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        )}
      </>
    );
  }, [
    addNewPanelTable,
    allFieldKeys,
    commitNewPanel,
    handleAddNewPanel,
    mode,
    newPanel.active,
    newPanel.groupName,
    newPanel.tables,
    removeNewPanelTable,
    updateNewPanelTableKeys,
    updateNewPanelTableTitle,
  ]);

  return (
    <>
      <Stack
        width="100%"
        marginTop="1.2rem"
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        height="4.8rem"
      >
        <Typography fontSize="2.2rem" fontWeight={500}>
          Review Panel Configuration
        </Typography>
        {mode === 'Edit' ? (
          <Stack direction="row" spacing={1} sx={{ pb: 0.5 }}>
            <Button variant="contained" onClick={saveReviewPanelConfig} sx={containedButtonStyle}>
              Save
            </Button>
            <Button variant="contained" onClick={handleCancel} sx={containedButtonStyle}>
              Cancel
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" spacing={0} sx={{ pb: 0.5 }}>
            <Tooltip title="Edit Review Panel Configuration" placement="top" arrow>
              <IconButton size="medium" onClick={handleEdit}>
                <Edit
                  fontSize="inherit"
                  sx={{ cursor: 'pointer', color: abyssColors.primary[500] }}
                />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>

      <FullWidthPanel>
        <Stack sx={{ flex: 1, gap: '2rem' }}>
          {reviewPanelConfig?.length ? (
            <>
              {reviewPanelConfig.map((group, groupIndex) => (
                <Stack key={`${group.groupKey}-${groupIndex}`}>
                  <>
                    <Stack key={group.groupKey} sx={{ gap: '1.5rem' }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        {mode === 'Edit' ? (
                          <TextField
                            label="Group name"
                            value={group.groupName}
                            onChange={(e) => updateGroupName(groupIndex, e.target.value)}
                            slotProps={{ inputLabel: { shrink: true } }}
                            sx={{ flex: 1, mr: 1 }}
                          />
                        ) : (
                          <Typography variant="h3" sx={{ mr: 1 }}>
                            {group.groupName}
                          </Typography>
                        )}

                        {mode === 'Edit' && (
                          <Tooltip title="Delete this group" placement="top" arrow>
                            <IconButton
                              size="medium"
                              onClick={() => {
                                setIsDeleteDialogOpen(true);
                                setDeleteGroupIds([...deleteGroupIds, groupIndex.toString()]);
                              }}
                            >
                              <Delete
                                fontSize="inherit"
                                sx={{ cursor: 'pointer', color: abyssColors.error[700] }}
                              />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                      <Divider />

                      <Grid container spacing={2}>
                        {group.groupSection.map((section, sectionIndex) => (
                          <Grid key={section?.sectionKey} size={{ xs: 12, sm: 6 }}>
                            <Stack
                              sx={{
                                p: 2,
                                gap: 1.25,
                                borderRadius: 1,
                                bgcolor: abyssColors.neutral[50],
                                border: `1px solid ${abyssColors.neutral[300]}`,
                              }}
                            >
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="flex-start"
                              >
                                <Stack
                                  direction="row"
                                  gap={1}
                                  alignItems="center"
                                  sx={{ flex: 1, minWidth: 0 }}
                                >
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Title:
                                  </Typography>

                                  {mode === 'Edit' ? (
                                    <TextField
                                      key={`${section?.id}-${groupIndex}-${sectionIndex}`}
                                      size="small"
                                      value={section?.title || ''}
                                      onChange={(e) =>
                                        updateSectionTitle(groupIndex, sectionIndex, e.target.value)
                                      }
                                      sx={{ flex: 1 }}
                                      slotProps={{
                                        input: {
                                          endAdornment: section?.title ? (
                                            <InputAdornment position="end">
                                              <IconButton
                                                size="small"
                                                edge="end"
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() =>
                                                  updateSectionTitle(groupIndex, sectionIndex, '')
                                                }
                                              >
                                                <CloseIcon fontSize="small" />
                                              </IconButton>
                                            </InputAdornment>
                                          ) : null,
                                        },
                                      }}
                                    />
                                  ) : (
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                      }}
                                    >
                                      {section?.title}
                                    </Typography>
                                  )}
                                </Stack>

                                {mode === 'Edit' && (
                                  <Tooltip title="Remove table">
                                    <IconButton
                                      size="small"
                                      onClick={() => removeTableFromGroup(groupIndex, sectionIndex)}
                                      sx={{ ml: 1 }}
                                    >
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Stack>

                              <Stack direction="row" gap={1} alignItems="flex-start">
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, pt: 0.5 }}>
                                  Field keys:
                                </Typography>

                                {mode === 'Edit' ? (
                                  <FormControl fullWidth>
                                    <InputLabel>Field keys</InputLabel>
                                    <Select
                                      multiple
                                      value={section?.fieldKeys}
                                      onChange={(e) =>
                                        updateSectionKeys(
                                          groupIndex,
                                          sectionIndex,
                                          typeof e.target.value === 'string'
                                            ? e.target.value.split(',').map((x) => x.trim())
                                            : (e.target.value as string[])
                                        )
                                      }
                                      input={<OutlinedInput label="Field keys" />}
                                      renderValue={(selected) => {
                                        const values = selected as string[];
                                        return (
                                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {values.map((v) => (
                                              <Chip
                                                key={v}
                                                label={v}
                                                size="small"
                                                color="primary"
                                                onMouseDown={(e) => e.stopPropagation()}
                                              />
                                            ))}
                                          </Box>
                                        );
                                      }}
                                      MenuProps={{
                                        PaperProps: {
                                          sx: { maxHeight: 300, overflowY: 'auto' },
                                        },
                                        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                        transformOrigin: { vertical: 'top', horizontal: 'left' },
                                        marginThreshold: 8,
                                        disableScrollLock: true,
                                      }}
                                    >
                                      {allFieldKeys.map((key) => {
                                        const checked = section?.fieldKeys.includes(key ?? '');
                                        return (
                                          <MenuItem key={key} value={key ?? ''}>
                                            <Checkbox checked={checked} />
                                            <ListItemText primary={key} />
                                          </MenuItem>
                                        );
                                      })}
                                    </Select>
                                  </FormControl>
                                ) : (
                                  <Typography
                                    sx={{
                                      wordBreak: 'break-word',
                                      whiteSpace: 'normal',
                                      flex: 1,
                                    }}
                                  >
                                    {section?.fieldKeys.join(', ')}
                                  </Typography>
                                )}
                              </Stack>
                            </Stack>
                          </Grid>
                        ))}
                      </Grid>

                      <Stack direction="row" justifyContent="flex-start">
                        <Button
                          startIcon={<AddCircleIcon />}
                          variant="contained"
                          onClick={() => addNewTableToGroup(groupIndex)}
                          disabled={mode === 'ReadOnly'}
                          sx={containedButtonStyle}
                        >
                          Add New Table to {group.groupName}
                        </Button>
                      </Stack>

                      <Divider />
                    </Stack>
                  </>
                </Stack>
              ))}
              {newPanelConfig}
            </>
          ) : (
            <>{newPanelConfig}</>
          )}
        </Stack>
      </FullWidthPanel>
      {isDeleteDialogOpen && (
        <GenericDialog
          open={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          title="Delete Group"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              Are you sure you want to delete this group? Deleting this will cause all corresponding
              tables to be deleted as well.
            </Typography>
          }
          actions={
            <>
              <Button
                variant="text"
                onClick={() => setIsDeleteDialogOpen(false)}
                sx={{ color: abyssColors.primary[500] }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => handleDeleteGroup()}
                sx={containedButtonStyle}
              >
                Delete
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}
    </>
  );
};
